<template>
  <div class="p-4">
    <div class="flex justify-center" v-if="isLoading">
      <LayupSpinner />
    </div>
    <div v-else class="flex justify-center">
      <div>
        <div
          v-if="(timerCount > 0 && debiCheckStatus === null) || (debiCheckStatus === '' && !checkCurrentTime)"
          class="w-full max-w-3xl bg-white p-6 rounded shadow"
        >
          <h1 class="text-xl font-bold text-center">Verifying Mandate</h1>
          <hr class="my-2" />
          <div class="flex justify-center">
            <div class="w-24 h-24 rounded-full border-4 border-blue-500 flex items-center justify-center">
              <div class="text-2xl font-semibold">{{ timerCount }}</div>
            </div>
          </div>
          <p class="text-center mt-4">Please wait while we verify your debit order mandate</p>
        </div>
        <div v-else class="w-full max-w-3xl bg-white p-6 rounded shadow">
          <h1 class="text-xl font-bold text-center">Confirm Your Mandate</h1>
          <hr class="my-2" />

          <div v-if="mandate.simulation !== true && checkCurrentTime">
            <p>Your debit order application will be submitted tomorrow at 8am...</p>
            <div class="flex justify-center">
              <button class="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700" @click="close()">
                Close
              </button>
            </div>
          </div>

          <div v-else-if="debiCheckStatus === 'Pending Authorisation' && time" class="text-center">
            <div v-if="isAcceptMandateLater">
              <p>You have until 9pm tomorrow to accept the debit order mandate...</p>
              <button class="mt-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-700" @click="close()">
                Close
              </button>
            </div>
            <div v-else>
              <div class="flex justify-center">
                <div class="w-40 h-40 rounded-full border-4 border-brand-500 flex items-center justify-center">
                  <div class="text-2xl font-semibold">{{ time }}</div>
                </div>
              </div>
              <p class="mt-4 text-center">Your online debit order request has been sent to your bank...</p>
            </div>
            <button
              v-if="!isAcceptMandateLater"
              class="mt-4 px-4 py-2 bg-brand-500 rounded hover:bg-brand-600 text-white"
              @click="willAcceptMandateLater()"
              data-testid="unable_to_accept_now"
            >
              Unable To Accept Now
            </button>
          </div>

          <div v-else-if="debiCheckStatus === 'Accepted'">
            <div class="flex justify-center">
              <SuccessIcon />
            </div>
            <p class="mt-4 text-center">You have successfully accepted your debit order. Thank you.</p>
            <div class="flex justify-center">
              <button class="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700" @click="close()">
                Close
              </button>
            </div>
          </div>

          <div v-else-if="debiCheckStatus === 'Rejected'" class="text-center">
            <div class="flex justify-center">
              <FailureIcon />
            </div>
            <div class="mt-4">
              <p>Your mandate was rejected. Please check that your bank details are correct.</p>
              <p v-for="(error, index) in debicheckFailedReasons" :key="index" class="font-bold text-sm text-red-600">
                {{ error }}
              </p>
            </div>
            <div class="flex gap-4 mt-4 flex justify-center">
              <button class="px-4 py-2 bg-brand-600 text-white rounded hover:bg-brand-700" @click="goToDebiCheck()">
                Amend Bank Details
              </button>
              <button class="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400" @click="close()">Cancel</button>
            </div>
          </div>

          <div v-else-if="mandate.simulation !== true" class="text-center">
            <p class="text-red-500">DebiCheck creation failed.</p>
            <p v-for="(error, index) in debicheckFailedReasons" :key="index" class="font-bold text-sm">
              {{ error }}
            </p>
            <div class="flex justify-center">
              <button class="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700" @click="close()">
                Close
              </button>
            </div>
          </div>

          <div v-if="debicheckSimulation" class="text-center">
            <hr class="mt-4" />
            <p class="mt-6 font-semibold">DEBICHECK SIMULATION RESPONSES:</p>
            <div v-if="simulationSubmitted && mandate.simulation" class="flex justify-center mt-2">
              <div class="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-500" />
            </div>
            <div v-else class="flex gap-4 mt-4 justify-center">
              <button
                class="px-4 py-2 bg-brand-600 text-white rounded hover:bg-brand-700"
                @click="simulatingDebiCheckResponse('Accepted')"
                data-testid="accept_mandate"
              >
                Approve
              </button>
              <button
                class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                @click="simulatingDebiCheckResponse('Rejected')"
                data-testid="reject_mandate"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import FailureIcon from '@/components/icons/FailureIcon.vue';
import SuccessIcon from '@/components/icons/SuccessIcon.vue';
import { CUSTOMER_UI_URL } from '@/config';
import { NetworkError } from '@/errors';
import { getMandate, getMandates, simulateMandateResponse } from '@/services/debiCheck';
import { useGeneralStore } from '@/stores/general';
import { useWidgetStore } from '@/stores/widget';
import { createException, createWarning } from '@/utils/monitoring.utils';
import dayjs from 'dayjs';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { ModelDebiCheckMandateListResponse, ModelDebiCheckMandateResponse } from '@layup/api-sdk';

const route = useRoute();
const router = useRouter();
const widgetStore = useWidgetStore();
const generalStore = useGeneralStore();

const mandateId = route?.params?.id as string;
const orderId = widgetStore.order?._id as string;
const timerCount = ref<number>(120);
const isAcceptMandateLater = ref(false);
const errorMessage = ref<string>('');
const isLoading = ref<boolean>(false);
const mandate = ref<ModelDebiCheckMandateResponse>({});
const simulationSubmitted = ref<boolean>(false);
const date = ref(dayjs().subtract(dayjs().hour() - 21, 'hours'));
const mandateList = ref<ModelDebiCheckMandateListResponse>({});

const init = async () => {
  try {
    isLoading.value = true;
    errorMessage.value = '';
    setInterval(() => {
      date.value = dayjs(date.value).subtract(1, 'seconds');
    }, 1000);

    const [mandateListResponse, mandateResponse] = await Promise.all([
      getMandates(mandateId?.toString()),
      getMandate(mandateId?.toString()),
    ]);

    mandate.value = mandateResponse;
    mandateList.value = mandateListResponse;
  } catch (error: any) {
    errorMessage.value = 'Error occurred while fetching mandate';
    if (error instanceof NetworkError) {
      createWarning('Network error occurred while fetching mandate.');
    } else {
      createException(error);
    }
    if (error?.response?.data) {
      errorMessage.value = error.response.data.message;
    }
  } finally {
    isLoading.value = false;
  }
};

const simulatingDebiCheckResponse = async (status: 'Accepted' | 'Rejected') => {
  await simulateMandateResponse(mandateId.toString(), status);
  await fetchMandateListForConfirmation();
};

const fetchMandateListForConfirmation = async () => {
  mandateList.value = await getMandates(mandateId?.toString());
};

const debiCheckStatus = computed(() => {
  return mandateList.value.status;
});

watch(
  debiCheckStatus,
  (newStatus) => {
    let pollingHandle: ReturnType<typeof setInterval> | null = null;
    let extendedPollingHandle: ReturnType<typeof setInterval> | null = null;

    // Initial 2-minute polling every 5 seconds
    pollingHandle = setInterval(async () => {
      await fetchMandateListForConfirmation();
    }, 5000);

    setTimeout(() => {
      if (pollingHandle) clearInterval(pollingHandle);
    }, 120000);

    // Extended polling if status is "Pending Authorisation"
    if (newStatus === 'Pending Authorisation') {
      if (pollingHandle) clearInterval(pollingHandle);

      extendedPollingHandle = setInterval(async () => {
        await fetchMandateListForConfirmation();
      }, 15000);

      setTimeout(() => {
        if (extendedPollingHandle) clearInterval(extendedPollingHandle);
      }, 1800000); // 30 minutes
    }

    // Stop polling immediately if status is "Rejected"
    if (newStatus === 'Rejected') {
      if (pollingHandle) clearInterval(pollingHandle);
      if (extendedPollingHandle) clearInterval(extendedPollingHandle);
    }

    // If accepted, stop polling and redirect after 5 seconds
    if (newStatus === 'Accepted') {
      widgetStore.mandateRequest = null;
      setTimeout(() => {
        close();
        if (pollingHandle) clearInterval(pollingHandle);
        if (extendedPollingHandle) clearInterval(extendedPollingHandle);
      }, 5000);
    }
  },
  { immediate: true },
);

const debicheckFailedReasons = computed(() => {
  return mandateList.value.debicheckFailedReasons;
});

const debicheckSimulation = computed(() => {
  return mandate.value.simulation;
});

const checkCurrentTime = computed(() => {
  const now = dayjs(mandate.value.createdAt || new Date());
  const hourToCheck = now.day() !== 0 ? 20 : 30;
  const dateToCheck = now.hour(hourToCheck).minute(30);
  return dayjs().isAfter(dateToCheck);
});

const time = computed(() => {
  return date.value.format('hh:mm:ss');
});

const goToDebiCheck = () => {
  router.replace(`/shopper/payment-plan/${orderId}/debi-check`);
};

const close = () => {
  if (generalStore.iFrame) {
    window.parent.postMessage({ type: 'layup-close' }, '*');
  } else {
    window.location.href = `${CUSTOMER_UI_URL}/dashboard/purchases/${orderId}`;
  }
};
const willAcceptMandateLater = () => {
  isAcceptMandateLater.value = true;
};

init();
</script>
